import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Shield,
  Settings,
  ChevronRight,
  Star,
  ArrowRight,
  LogOut,
  UserCheck,
  Loader2,
  MessageSquare,
  Download,
  GraduationCap,
  Lock,
  AlertCircle,
  Check,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useStudent } from "../../context/StudentContext";
import { ThemeSelector } from "../../context/ThemeContext";
import { getDataSaver, getReaderDirection, setDataSaver, setReaderDirection, type ReaderDirection } from "../../lib/readerPrefs";
import { api, ApiError, type CatalogStream } from "../../lib/api";
import { mapApiError } from "../../components/ui/StatusMessage";
import { AdminSetupModal } from "../../components/student/AdminSetupModal";
import { FeedbackModal } from "../../components/student/FeedbackModal";
import { usePWAInstall } from "../../lib/usePWAInstall";
import { ProfileStats } from "../../components/student/ProfileStats";

export function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tokens, logout } = useAuth();
  const { isPremium, openUpgrade, setIsPremium } = useStudent();
  const [profile, setProfile] = useState<{
    fullName: string;
    phone?: string;
    university: string | null;
    department: string | null;
    streamName?: string | null;
    avatar: string;
    isPremium: boolean;
  } | null>(null);
  const [paymentBanner, setPaymentBanner] = useState<{
    tone: "success" | "pending" | "error";
    text: string;
  } | null>(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    if (!tokens?.accessToken) return;
    api.profile(tokens.accessToken).then((p) => {
      setProfile({
        fullName: p.fullName,
        phone: p.phone,
        university: p.university,
        department: p.department,
        streamName: p.streamName,
        avatar: p.avatar,
        isPremium: p.isPremium,
      });
      setIsPremium(p.isPremium);
    }).catch(() => {});
  }, [tokens?.accessToken, setIsPremium]);

  // Payment return handling: poll status on redirect
  useEffect(() => {
    const payment = searchParams.get("payment");
    const txRef = searchParams.get("tx_ref");
    if (!tokens?.accessToken || !payment || !txRef) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = payment === "success" ? 4 : 6;

    async function tick() {
      if (cancelled || !tokens?.accessToken || !txRef) return;
      setCheckingPayment(true);
      try {
        const status = await api.payments.status(tokens.accessToken, txRef, true);
        if (cancelled) return;
        if (status.status === "success" || status.isPremium) {
          setIsPremium(true);
          setPaymentBanner({ tone: "success", text: status.message || "Payment confirmed — premium is active." });
          setProfile((prev) => (prev ? { ...prev, isPremium: true } : prev));
          setSearchParams({}, { replace: true });
          setCheckingPayment(false);
          return;
        }
        if (status.status === "failed") {
          setPaymentBanner({ tone: "error", text: status.message || "Payment failed. You can try again." });
          setSearchParams({}, { replace: true });
          setCheckingPayment(false);
          return;
        }
        setPaymentBanner({
          tone: "pending",
          text: status.message || "Payment is still pending — verifying…",
        });
      } catch {
        if (!cancelled) {
          setPaymentBanner({
            tone: "pending",
            text: "We received your return — verifying payment. Premium unlocks automatically when confirmed.",
          });
        }
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(tick, attempts < 2 ? 1200 : 2500);
      } else if (!cancelled) {
        setCheckingPayment(false);
        setPaymentBanner((prev) => prev ?? {
          tone: "pending",
          text: "Payment is still being checked. Refresh this page in a minute, or open Help if you need assistance.",
        });
      }
    }

    tick();
    return () => { cancelled = true; };
  }, [tokens?.accessToken, searchParams, setSearchParams, setIsPremium]);

  function handleSignOut() {
    logout();
    navigate("/auth/login");
  }

  return (
    <div className="max-w-2xl space-y-6">
      {paymentBanner && (
        <div
          className={`rounded-sm border px-4 py-3 text-xs sm:text-sm font-medium flex items-start gap-2 ${
            paymentBanner.tone === "success"
              ? "bg-success/10 border-success/30 text-success"
              : paymentBanner.tone === "error"
                ? "bg-destructive/10 border-destructive/30 text-destructive"
                : "bg-warning/10 border-warning/30 text-warning"
          }`}
        >
          {checkingPayment && <Loader2 size={16} className="animate-spin mt-0.5 shrink-0" />}
          <span>{paymentBanner.text}</span>
        </div>
      )}

      {/* Main Student Profile Card */}
      <div className="bg-card rounded-sm border border-border p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 shadow-xs">
        <div className="w-16 h-16 bg-primary/10 border border-primary/20 text-primary rounded-xs flex items-center justify-center shrink-0">
          <span className="text-xl font-bold font-display">{profile?.avatar ?? "??"}</span>
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <h2 className="text-xl font-bold font-display text-foreground truncate">{profile?.fullName ?? "Student"}</h2>
          <div className="text-xs text-muted-foreground font-benton space-y-0.5">
            <p className="truncate">
              {profile?.university ?? "University student"}
              {profile?.department ? ` · ${profile.department}` : ""}
            </p>
            {profile?.phone && <p className="text-xs text-muted-foreground">Phone: {profile.phone}</p>}
            {profile?.streamName && (
              <span className="inline-block mt-1 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-xs bg-secondary text-primary border border-border">
                Stream: {profile.streamName}
              </span>
            )}
          </div>
          <div>
            {isPremium ? (
              <div className="mt-2 inline-flex items-center gap-1.5 bg-primary/10 text-primary border border-primary/20 px-2.5 py-0.5 rounded-xs text-xs font-semibold">
                <Star size={12} fill="currentColor" /> Premium member
              </div>
            ) : (
              <button onClick={() => openUpgrade()} className="mt-2 inline-flex items-center gap-1.5 text-primary text-xs font-semibold hover:underline">
                Free plan · Upgrade to Premium <ArrowRight size={13} />
              </button>
            )}
          </div>
        </div>
      </div>

      <ProfileStats />

      {/* Navigation Card */}
      <div className="bg-card rounded-sm border border-border overflow-hidden divide-y divide-border shadow-xs">
        <button
          type="button"
          onClick={() => navigate("/student/settings")}
          className="w-full flex items-center gap-4 px-5 py-4 hover:bg-muted/60 transition text-left"
        >
          <div className="w-8 h-8 rounded-xs bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Shield size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-xs sm:text-sm text-foreground">Settings & Security</p>
            <p className="text-xs text-muted-foreground">Manage your password, theme, and account</p>
          </div>
          <ChevronRight size={15} className="text-muted-foreground" />
        </button>
      </div>

      <button
        type="button"
        onClick={handleSignOut}
        className="flex items-center justify-center gap-2 px-5 py-3.5 bg-card rounded-sm border border-border text-destructive font-semibold text-xs sm:text-sm w-full hover:bg-destructive/10 transition"
      >
        <LogOut size={16} />
        <span>Sign out</span>
      </button>
    </div>
  );
}

export function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const { tokens, logout } = useAuth();
  const { openUpgrade, isPremium } = useStudent();
  const [readerDir, setReaderDirState] = useState<ReaderDirection>(getReaderDirection);
  const [saver, setSaverState] = useState<boolean>(getDataSaver);
  const token = tokens?.accessToken;

  const [profile, setProfile] = useState<{
    fullName: string;
    phone?: string;
    university: string | null;
    streamId?: string | null;
    streamName?: string | null;
    currentSemester?: number;
    canChangeStream?: boolean;
    role?: string;
    pendingAdminSetup?: boolean;
  } | null>(null);

  const [streams, setStreams] = useState<CatalogStream[]>([]);
  const [fullName, setFullName] = useState("");
  const [streamId, setStreamId] = useState("");
  const [currentSemester, setCurrentSemester] = useState<number>(1);
  const [canChangeStream, setCanChangeStream] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const { canInstall, install } = usePWAInstall();

  useEffect(() => {
    if (!token) return;
    Promise.all([
      api.profile(token),
      api.streams().catch(() => [] as CatalogStream[]),
    ])
      .then(([p, stList]) => {
        setProfile(p);
        setFullName(p.fullName || "");
        setStreamId(p.streamId || "");
        setCurrentSemester(p.currentSemester || 1);
        setCanChangeStream(p.canChangeStream ?? true);
        setStreams(stList);
      })
      .catch(() => {});
  }, [token]);

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const updated = await api.updateProfile(token, {
        fullName: fullName.trim(),
        streamId: streamId || undefined,
        currentSemester,
      });
      setProfile((prev) => (prev ? { ...prev, ...updated } : prev));
      setCanChangeStream(updated.canChangeStream ?? true);
      setSaveSuccess(true);
      if (returnTo) {
        setTimeout(() => {
          navigate(returnTo, { replace: true });
        }, 600);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setSaveError(mapApiError(err).message);
      } else {
        setSaveError("We could not update your profile. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold font-display text-foreground">Settings</h2>
        <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
          Manage your student profile, academic stream, appearance, and account security.
        </p>
      </div>

      {returnTo && (
        <div className="bg-primary/10 border border-primary/20 rounded-sm p-4 flex items-center justify-between gap-3">
          <div className="space-y-0.5">
            <p className="text-xs font-bold text-foreground">Complete your academic stream</p>
            <p className="text-xs text-muted-foreground">
              Please select and save your stream below to continue with your Premium upgrade.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate(returnTo, { replace: true })}
            className="btn btn-secondary text-xs py-1.5 px-3 shrink-0"
          >
            Back to Premium
          </button>
        </div>
      )}

      {/* ── 1. Academic Profile & Stream (Editable) ── */}
      <div className="bg-card rounded-sm border border-border p-5 space-y-4 shadow-xs">
        <div className="flex items-center justify-between pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <GraduationCap size={16} className="text-primary" />
            <h3 className="font-bold text-sm text-foreground">Academic Profile</h3>
          </div>
          <span className="text-xs text-muted-foreground">
            University: {profile?.university || "Standard Curriculum"}
          </span>
        </div>

        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">
              Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="field text-xs py-2 px-3 rounded-xs w-full"
              placeholder="Your full name"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-foreground">
                Academic Stream
              </label>
              {!canChangeStream && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-warning">
                  <Lock size={11} /> Locked by active entitlement
                </span>
              )}
            </div>

            {canChangeStream ? (
              <select
                value={streamId}
                onChange={(e) => setStreamId(e.target.value)}
                required
                className="field text-xs py-2 px-3 rounded-xs w-full bg-card"
              >
                <option value="">Select your stream…</option>
                {streams.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.code.toUpperCase()})
                  </option>
                ))}
              </select>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={profile?.streamName || "Current Stream"}
                  disabled
                  className="field text-xs py-2 px-3 rounded-xs w-full bg-muted/60 opacity-80 cursor-not-allowed"
                />
                <div className="p-2.5 rounded-xs bg-warning/10 border border-warning/20 text-xs text-muted-foreground flex items-start gap-2">
                  <AlertCircle size={14} className="text-warning shrink-0 mt-0.5" />
                  <p className="leading-relaxed">
                    Stream cannot be changed while you have an active Premium entitlement tied to your stream. If you need assistance, please contact support at{" "}
                    <a
                      href="https://t.me/Soorgaa_bot"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary font-semibold underline"
                    >
                      https://t.me/Soorgaa_bot
                    </a>.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">
              Current Semester
            </label>
            <select
              value={currentSemester}
              onChange={(e) => setCurrentSemester(Number(e.target.value))}
              className="field text-xs py-2 px-3 rounded-xs w-full bg-card"
            >
              <option value={1}>Semester 1 (Freshman Sem I)</option>
              <option value={2}>Semester 2 (Freshman Sem II)</option>
            </select>
          </div>

          {saveError && (
            <div className="p-2.5 rounded-xs bg-destructive/10 border border-destructive/20 text-xs text-destructive flex items-start gap-2">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <div className="flex-1">
                <span>{saveError}</span>
                {saveError.includes("t.me/Soorgaa_bot") && (
                  <div className="mt-1">
                    <a
                      href="https://t.me/Soorgaa_bot"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                    >
                      Contact Support on Telegram
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {saveSuccess && (
            <div className="p-2.5 rounded-xs bg-success/10 border border-success/20 text-xs text-success flex items-center gap-2">
              <Check size={14} />
              <span>
                {returnTo
                  ? "Profile updated! Returning to Premium checkout…"
                  : "Profile updated successfully."}
              </span>
            </div>
          )}

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary px-4 py-2 text-xs font-semibold rounded-xs flex items-center gap-1.5"
            >
              {saving && <Loader2 size={13} className="animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>

      {profile?.pendingAdminSetup && (
        <div className="bg-warning/10 border border-warning/30 rounded-sm p-4 space-y-2">
          <div className="flex items-center gap-2 text-warning font-bold text-sm">
            <Shield size={16} />
            <span>Administrator Promotion Notice</span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            You have been promoted to an Administrator on Soorgaa. Before you can access the admin dashboard, you must verify your identity through Telegram and enroll an administrator password.
          </p>
          <button
            type="button"
            onClick={() => setShowSetupModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-on-accent text-xs font-semibold rounded-xs shadow-xs hover:bg-primary/90 transition cursor-pointer"
          >
            Enroll Admin Password <ArrowRight size={13} />
          </button>
        </div>
      )}

      {profile?.role === "admin" && !profile?.pendingAdminSetup && (
        <div className="bg-primary/10 border border-primary/20 rounded-sm p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-primary font-bold text-sm">
              <Shield size={16} />
              <span>Administrator Account Active</span>
            </div>
            <a
              href="/admin"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              Go to Admin Portal <ArrowRight size={12} />
            </a>
          </div>
          <p className="text-xs text-muted-foreground">
            Your account has administrator privileges. You can manage courses, resources, and platform settings.
          </p>
        </div>
      )}

      {/* ── Install App (PWA) ── */}
      {canInstall && (
        <div className="bg-primary/10 border border-primary/20 rounded-sm p-4 flex items-center justify-between gap-4">
          <div>
            <h4 className="font-bold text-xs sm:text-sm text-foreground">Install Soorgaa App</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add Soorgaa to your home screen for quick offline access and a faster mobile experience.
            </p>
          </div>
          <button
            type="button"
            onClick={install}
            className="btn btn-primary px-3 py-1.5 text-xs font-semibold rounded-xs shrink-0 flex items-center gap-1.5"
          >
            <Download size={13} /> Install
          </button>
        </div>
      )}

      {/* ── 2. Appearance / Theme (Functional) ── */}
      <div className="bg-card rounded-sm border border-border p-5 space-y-3 shadow-xs">
        <div className="flex items-center gap-2">
          <Settings size={16} className="text-primary" />
          <h3 className="font-bold text-sm text-foreground">Appearance</h3>
        </div>
        <p className="text-xs text-muted-foreground">Choose your interface theme style.</p>
        <ThemeSelector />
      </div>

      {/* Data saver (stored on this device) */}
      <div className="bg-card rounded-sm border border-border p-5 space-y-3 shadow-xs">
        <h3 className="font-bold text-sm text-foreground">Data saver</h3>
        <p className="text-xs text-muted-foreground">Images in notes load only when you tap them, and modules load one page at a time.</p>
        <label className="flex min-h-11 items-center gap-3 text-sm">
          <input
            type="checkbox"
            checked={saver}
            onChange={(e) => {
              setDataSaver(e.target.checked);
              setSaverState(e.target.checked);
            }}
          />
          Use less data
        </label>
      </div>

      {/* Module reading direction (stored on this device) */}
      <div className="bg-card rounded-sm border border-border p-5 space-y-3 shadow-xs">
        <h3 className="font-bold text-sm text-foreground">Module reading direction</h3>
        <p className="text-xs text-muted-foreground">How pages turn when you read a module online. Applies the next time you open a module.</p>
        <div className="flex gap-2" role="radiogroup" aria-label="Module reading direction">
          {(["horizontal", "vertical"] as const).map((d) => (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={readerDir === d}
              onClick={() => {
                setReaderDirection(d);
                setReaderDirState(d);
              }}
              className={`min-h-11 flex-1 rounded-btn border text-sm font-medium focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${readerDir === d ? "border-primary bg-accent-soft text-primary" : "border-border"}`}
            >
              {d === "horizontal" ? "Horizontal" : "Vertical"}
            </button>
          ))}
        </div>
      </div>

      {/* ── 3. Account & Security (Functional) ── */}
      <div className="bg-card rounded-sm border border-border p-5 space-y-5 shadow-xs">
        <div className="flex items-center justify-between pb-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-primary" />
            <h3 className="font-bold text-sm text-foreground">Account & Security</h3>
          </div>
          <span className="text-xs font-bold px-2 py-0.5 rounded-xs bg-success/10 text-success border border-success/30 flex items-center gap-1">
            <UserCheck size={12} /> Active Account
          </span>
        </div>

        {/* User Summary Info */}
        <div className="bg-muted/40 rounded-xs p-3.5 border border-border/70 space-y-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Full Name:</span>
            <span className="font-semibold text-foreground">{profile?.fullName ?? "—"}</span>
          </div>
          {profile?.phone && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Phone Number:</span>
              <span className="font-semibold text-foreground font-mono">{profile.phone}</span>
            </div>
          )}
          {profile?.university && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">University:</span>
              <span className="font-semibold text-foreground">{profile.university}</span>
            </div>
          )}
          {profile?.streamName && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Stream:</span>
              <span className="font-semibold text-primary">{profile.streamName}</span>
            </div>
          )}
        </div>

        <div className="pt-2 border-t border-border/60 space-y-1.5 text-xs">
          <div className="flex justify-between items-center text-muted-foreground">
            <span>Authentication:</span>
            <span className="font-medium text-foreground">Telegram OTP (Passwordless)</span>
          </div>
          <div className="flex justify-between items-center text-muted-foreground">
            <span>Device Session Control:</span>
            <span className="font-medium text-success">Single Active Device Protected</span>
          </div>
          <p className="text-xs text-muted-foreground pt-1 leading-relaxed">
            Students sign in securely via one-time passcodes sent to linked Telegram. Logging in on a new device automatically expires older active sessions to keep your account safe.
          </p>
        </div>
      </div>

      {/* ── 3. Rating & Feedback (Manual) ── */}
      <div className="bg-card rounded-sm border border-border p-5 space-y-3 shadow-xs">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} className="text-primary" />
          <h3 className="font-bold text-sm text-foreground">Rating & Feedback</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Tell us about your learning experience or suggest new features to help us improve.
        </p>
        <button
          type="button"
          onClick={() => setShowFeedbackModal(true)}
          className="btn btn-primary px-4 py-2 text-xs font-semibold rounded-xs flex items-center gap-1.5"
        >
          <Star size={13} fill="currentColor" /> Rate the App
        </button>
      </div>

      {/* ── 4. Membership / Upgrade ── */}
      {!isPremium && (
        <button
          type="button"
          onClick={() => openUpgrade()}
          className="w-full bg-card p-4 rounded-sm border border-border flex items-center justify-between hover:bg-muted/60 transition text-left text-primary font-semibold text-xs sm:text-sm shadow-xs"
        >
          <div className="flex items-center gap-2">
            <Star size={16} fill="currentColor" />
            <span>Upgrade to Premium Membership</span>
          </div>
          <ChevronRight size={15} />
        </button>
      )}

      {/* ── 4. Sign Out ── */}
      <button
        type="button"
        onClick={() => {
          logout();
          navigate("/auth/login");
        }}
        className="w-full bg-card p-4 rounded-sm border border-border flex items-center justify-between hover:bg-destructive/10 transition text-left text-destructive font-semibold text-xs sm:text-sm shadow-xs"
      >
        <div className="flex items-center gap-2">
          <LogOut size={16} />
          <span>Sign out of account</span>
        </div>
        <ChevronRight size={15} />
      </button>

      <AdminSetupModal
        open={showSetupModal}
        phone={profile?.phone}
        onClose={() => setShowSetupModal(false)}
        onCompleted={() => {
          setShowSetupModal(false);
          if (token) {
            api.profile(token).then((p) => {
              setProfile((prev) => (prev ? { ...prev, role: p.role, pendingAdminSetup: p.pendingAdminSetup } : prev));
            });
          }
        }}
      />

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        triggerType="manual"
        title="Rate Soorgaa"
        subtitle="Your feedback directly shapes our platform updates for Ethiopian university students."
      />
    </div>
  );
}
